<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

import GlobePanel from "@/globe/components/GlobePanel.vue";
import PlainLayout from "@/layouts/PlainLayout.vue";

const AUTO_REFRESH_INTERVAL_MS = 21600000;

definePage({
  meta: { layout: PlainLayout, auth: [] },
});

const KIOSK_SLIDES = [
  {
    id: "globe",
    kind: "component",
    component: GlobePanel,
    durationMs: 120000,
  },
  {
    id: "grafana",
    kind: "iframe",
    url: "https://grafana-embed.mws.com/public-dashboards/0c3e0a6a12fe45eda9cd2da675a25ef1?disableAutoLogin=true&theme=dark&timezone=browser&kiosk=tv",
    durationMs: 30000,
  },
] as const;

const activeIndex = ref(0);
const activeSlideId = computed(() => KIOSK_SLIDES[activeIndex.value]?.id ?? "");

let switchTimer: ReturnType<typeof setTimeout> | null = null;
let reloadTimer: ReturnType<typeof setInterval> | null = null;

const scheduleNext = (): void => {
  const current = KIOSK_SLIDES[activeIndex.value];
  if (!current) return;

  switchTimer = setTimeout(() => {
    activeIndex.value = (activeIndex.value + 1) % KIOSK_SLIDES.length;
    scheduleNext();
  }, current.durationMs);
};

onMounted(() => {
  if (KIOSK_SLIDES.length > 1) {
    scheduleNext();
  }

  reloadTimer = setInterval(() => {
    window.location.reload();
  }, AUTO_REFRESH_INTERVAL_MS);
});

onUnmounted(() => {
  if (switchTimer) {
    clearTimeout(switchTimer);
    switchTimer = null;
  }

  if (reloadTimer) {
    clearInterval(reloadTimer);
    reloadTimer = null;
  }
});
</script>

<template>
  <div
    class="fixed inset-0 h-screen w-screen overflow-hidden bg-black cursor-none"
  >
    <div
      v-for="slide in KIOSK_SLIDES"
      :key="slide.id"
      class="absolute inset-0 transition-opacity duration-700 ease-out"
      :class="
        slide.id === activeSlideId
          ? 'opacity-100'
          : 'opacity-0 pointer-events-none'
      "
      :aria-hidden="slide.id !== activeSlideId"
    >
      <component
        :is="slide.component"
        v-if="slide.kind === 'component'"
      />
      <iframe
        v-else
        :src="slide.url"
        title="Kiosk slide"
        class="h-full w-full border-0 bg-black"
        allow="fullscreen"
      />
    </div>
  </div>
</template>
